import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';

import { Container, Col, Tabs, Tab, Row, Spinner } from 'react-bootstrap';
import { Match, MatchStatus } from '../../models/interfaces/Match';
import { Competition } from '../../models/interfaces/Competition';
import {
    CompetitionAdmin,
    CompetitionMatchManager,
} from '../../models/interfaces/Competitions/CompetitionAdmin';
import { SportProvider } from '../../models/interfaces/SportProvider';
import { MatchStatList } from './MatchStatList';
import MatchBoxScoreView from './MatchBoxScoreView';
import MatchTeamsTable from './MatchTeamsTable';
import MatchSummaryView from './MatchSummaryView';
import { User } from '../../models/interfaces/User';
import CompetitionMatchAdminView from './CompetitionMatchAdminView';
import useMatchStats from '../../helpers/useMatchStats';
import MatchChipView from './MatchChipView';

interface Props {
    user?: User;
    match: Match;
    sportProvider: SportProvider;
}

export default function MatchDetailPage({ user, match, sportProvider }: Props) {
    const [hasLoadedCompetitionAdmin, setHasLoadedCompetitionAdmin] = useState<Boolean>(false);
    const [competitionAdmin, setCompetitionAdmin] = useState<CompetitionMatchManager | undefined>();

    const [competition] = useState<Competition | undefined>(match.competition);
    const [finalScoreEntered, setFinalScoreEntered] = useState<boolean>(false);
    const history = useHistory();

    //custom hook
    const { matchStatsList, lightTeamScore, darkTeamScore, winningTeam, triggerUpdatedMatchStats } =
        useMatchStats({ match, updateScoresImmediately: !!competitionAdmin });

    useEffect(() => {
        if (match.status === MatchStatus.completed) {
            if (
                matchStatsList &&
                matchStatsList.length === 0 &&
                ((lightTeamScore && lightTeamScore > 0) || (darkTeamScore && darkTeamScore > 0))
            ) {
                setFinalScoreEntered(true);
            } else {
                setFinalScoreEntered(false);
            }
        }
    }, [match.status, lightTeamScore, darkTeamScore, matchStatsList]);

    useEffect(() => {
        if (user && competition && !hasLoadedCompetitionAdmin) {
            competition
                .adminForUser(user)
                .then((competitionAdmin) => {
                    const matchManager = competitionAdmin.matchManager;
                    setCompetitionAdmin(matchManager);
                })
                .catch(() => {
                    setCompetitionAdmin(undefined);
                })
                .finally(() => {
                    setHasLoadedCompetitionAdmin(true);
                });
        }
    }, [competition, user, hasLoadedCompetitionAdmin]);

    const showScoreSheet = matchStatsList && matchStatsList.length !== 0 ? true : false;

    return (
        <Container>
            <Row className="justify-content-md-center pt-4">
                <Col>
                    <MatchChipView
                        match={match}
                        winningTeam={winningTeam}
                        sportProvider={sportProvider}
                        user={user}
                        showScoreSheet={showScoreSheet}
                    />
                </Col>
                <Col lg={8}>
                    <MatchBoxScoreView
                        match={match}
                        matchStatsList={matchStatsList}
                        sportProvider={sportProvider}
                        lightTeamScore={lightTeamScore}
                        darkTeamScore={darkTeamScore}
                    />
                </Col>
            </Row>
            {match.status !== MatchStatus.scheduled ? (
                <Row>
                    <Col>
                        {!matchStatsList ? (
                            <Spinner
                                as="span"
                                animation="border"
                                size="sm"
                                role="status"
                                aria-hidden="true"
                            />
                        ) : (
                            <Tabs>
                                {/* below four tabs only render on same condition but tab can be a child of a react fragment so need to repeat selector */}
                                <Tab title="Summary" eventKey="summary">
                                    {matchStatsList.length > 0 ? (
                                        <MatchSummaryView match={match} statList={matchStatsList} />
                                    ) : match.status === MatchStatus.completed ? (
                                        <div>Only final score is available to display</div>
                                    ) : (
                                        <div>
                                            No game stats have been recorded. Please check back
                                            later
                                        </div>
                                    )}
                                </Tab>
                                {matchStatsList.length > 0 && (
                                    <Tab title={match.lightCapTeam.name} eventKey="lightCapStats">
                                        <MatchTeamsTable
                                            match={match}
                                            matchStatsList={matchStatsList}
                                            teamIDFilter={match.lightCapTeam.id}
                                            sportProvider={sportProvider}
                                        />
                                    </Tab>
                                )}
                                {matchStatsList.length > 0 && (
                                    <Tab title={match.darkCapTeam.name} eventKey="darkCapStats">
                                        <MatchTeamsTable
                                            match={match}
                                            matchStatsList={matchStatsList}
                                            teamIDFilter={match.darkCapTeam.id}
                                            sportProvider={sportProvider}
                                        />
                                    </Tab>
                                )}
                                {matchStatsList.length > 0 && (
                                    <Tab title="Play-by-Play" eventKey="play-by-play">
                                        <MatchStatList
                                            matchStatsList={matchStatsList}
                                            shouldDisplayStat={(stat) => {
                                                return match.fanViewConfiguration.shouldDisplayStat(
                                                    stat
                                                );
                                            }}
                                            currentMatchStatus={match.status}
                                        />
                                    </Tab>
                                )}
                                {competitionAdmin && user && (
                                    <Tab title="Admin" eventKey="admin">
                                        <CompetitionMatchAdminView
                                            competitionAdmin={competitionAdmin}
                                            match={match}
                                            onDelete={() => {
                                                history.push(
                                                    `/competitions/${match.competition.id}`
                                                );
                                            }}
                                            user={user}
                                            matchStatsList={matchStatsList}
                                            sportProvider={sportProvider}
                                            triggerUpdatedMatchStats={triggerUpdatedMatchStats}
                                            finalScoreEntered={finalScoreEntered}
                                        />
                                    </Tab>
                                )}
                            </Tabs>
                        )}
                    </Col>
                </Row>
            ) : (
                competitionAdmin &&
                user && (
                    <CompetitionMatchAdminView
                        competitionAdmin={competitionAdmin}
                        match={match}
                        onDelete={() => {
                            history.push(`/competitions/${match.competition.id}`);
                        }}
                        user={user}
                        matchStatsList={matchStatsList}
                        sportProvider={sportProvider}
                        triggerUpdatedMatchStats={triggerUpdatedMatchStats}
                        finalScoreEntered={finalScoreEntered}
                    />
                )
            )}
        </Container>
    );
}
